import React from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { Fader } from '../components/Fader';
import { Secuence } from '../components/Secuence';

const styles = theme => ({
  root: {},
  container: {
    position: 'fixed',
    left: 0,
    right: 0,
    width: '100vw',
    zIndex: 1,
    // Position between header and footer
    top: 'var(--header-height, 0px)',
    bottom: 'var(--footer-height, 0px)',
    height: 'calc(100vh - var(--header-height, 0px) - var(--footer-height, 0px))',
    // Mobile-specific fixes for Safari/Chrome iOS
    '@media (max-width: 1024px)': {
      // On mobile/tablet, footer is hidden, so iframe spans from header to bottom
      bottom: 0,
      // Use dynamic viewport height which accounts for mobile browser UI
      height: 'calc(100dvh - var(--header-height, 0px))',
      // Fallback for browsers that don't support dvh
      '@supports not (height: 100dvh)': {
        height: 'calc(var(--viewport-height, 100vh) - var(--header-height, 0px))'
      },
      // Ensure proper overflow handling
      overflow: 'hidden',
      maxHeight: 'calc(100dvh - var(--header-height, 0px))',
      // Fallback for maxHeight
      '@supports not (max-height: 100dvh)': {
        maxHeight: 'calc(var(--viewport-height, 100vh) - var(--header-height, 0px))'
      }
    }
  },
  iframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    // Mobile/tablet-specific iframe fixes
    '@media (max-width: 1024px)': {
      // Prevent iframe from expanding beyond container on iOS
      maxHeight: '100%',
      // Improve rendering performance on mobile
      transform: 'translateZ(0)',
      '-webkit-transform': 'translateZ(0)',
      backfaceVisibility: 'hidden',
      '-webkit-backfaceVisibility': 'hidden'
    }
  },
  // Override Header and Footer to be fixed when on map page
  '@global': {
    'body[data-page="map"] header': {
      position: 'fixed !important',
      top: '0 !important',
      left: '0 !important',
      right: '0 !important',
      zIndex: '1000 !important',
      width: '100% !important'
    },
    'body[data-page="map"] footer': {
      position: 'fixed !important',
      bottom: '0 !important',
      left: '0 !important',
      right: '0 !important',
      zIndex: '1000 !important',
      width: '100% !important',
      // Mobile-specific: Hide footer on mobile/tablet screens
      '@media (max-width: 1024px)': {
        display: 'none !important'
      }
    },
    // Prevent body from scrolling on map page in mobile/tablet
    '@media (max-width: 1024px)': {
      'body[data-page="map"]': {
        overflow: 'hidden',
        position: 'fixed',
        width: '100%',
        height: '100dvh',
        // Disable pinch-to-zoom and double-tap zoom on map page only
        touchAction: 'none',
        // Fallback for browsers that don't support dvh
        '@supports not (height: 100dvh)': {
          height: 'var(--viewport-height, 100vh)'
        }
      },
      'body[data-page="map"] html': {
        overflow: 'hidden',
        height: '100dvh',
        // Fallback for browsers that don't support dvh
        '@supports not (height: 100dvh)': {
          height: 'var(--viewport-height, 100vh)'
        }
      }
    }
  }
});

class Music extends React.Component {
  static propTypes = {
    classes: PropTypes.object
  };

  state = {
    iframeLoaded: false
  };

  componentDidMount () {
    // Add data attribute to body for page-specific styling
    document.body.setAttribute('data-page', 'map');

    // Calculate and set header and footer heights
    this.updateDimensions();

    // Update dimensions on window resize with throttling
    window.addEventListener('resize', this.throttledUpdateDimensions);

    // Mobile Safari specific: Update dimensions on scroll end and orientation change
    window.addEventListener('orientationchange', this.handleOrientationChange);

    // Listen to visualViewport resize events (for mobile browser UI changes)
    if (window.visualViewport) {
      this.handleVisualViewportResize = () => {
        this.throttledUpdateDimensions();
      };
      window.visualViewport.addEventListener('resize', this.handleVisualViewportResize);
    }

    // For iOS Safari, also update when scrolling stops (address bar hide/show)
    let scrollTimer = null;
    this.handleScroll = () => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      scrollTimer = setTimeout(() => {
        this.updateDimensions();
      }, 150);
    };
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Lazy load iframe after a short delay to let page animations complete
    // Increased delay for mobile to ensure dimensions are calculated
    this.iframeLoadTimeout = setTimeout(() => {
      this.updateDimensions(); // Update one more time before showing iframe
      this.setState({ iframeLoaded: true });
    }, 500);
  }

  componentWillUnmount () {
    // Clean up the data attribute when leaving the page
    document.body.removeAttribute('data-page');

    // Remove resize listener
    window.removeEventListener('resize', this.throttledUpdateDimensions);

    // Remove orientation change listener
    window.removeEventListener('orientationchange', this.handleOrientationChange);

    // Remove visualViewport resize listener
    if (window.visualViewport && this.handleVisualViewportResize) {
      window.visualViewport.removeEventListener('resize', this.handleVisualViewportResize);
    }

    // Remove scroll listener
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }

    // Clear timeout
    if (this.iframeLoadTimeout) {
      clearTimeout(this.iframeLoadTimeout);
    }

    // Clear throttle timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  // Throttled version to prevent excessive reflows
  throttledUpdateDimensions = () => {
    if (this.resizeTimeout) {
      return;
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateDimensions();
      this.resizeTimeout = null;
    }, 150);
  }

  // Handle orientation change for mobile devices
  handleOrientationChange = () => {
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.updateDimensions();
      }, 300); // Wait for orientation change to complete
    });
  }

  updateDimensions = () => {
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    const isMobile = window.innerWidth <= 1024;

    if (header) {
      // Use getBoundingClientRect for more accurate measurements on mobile
      const headerHeight = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }

    if (footer) {
      // On mobile/tablet, footer is hidden so set height to 0
      // On desktop, use actual footer height
      const footerHeight = isMobile ? 0 : footer.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
    }

    // For mobile browsers: Use visualViewport API for accurate viewport height
    // This accounts for dynamic browser UI (address bar, toolbar)
    if (isMobile && window.visualViewport) {
      const viewportHeight = window.visualViewport.height;
      document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
    } else if (isMobile) {
      // Fallback for browsers without visualViewport support
      const viewportHeight = window.innerHeight;
      document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
    }
  }

  render () {
    const { classes } = this.props;
    const { iframeLoaded } = this.state;

    return (
      <Main className={classes.root}>
        <Secuence stagger>
          <Fader className={classes.container}>
            {iframeLoaded && (
              <iframe
                className={classes.iframe}
                src='https://eleksyonph.vercel.app'
                title='Embedded Content'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                allowFullScreen
                loading='lazy'
              />
            )}
          </Fader>
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(Music);
